import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Skeleton,
  Table,
  Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { ArrowLeftOutlined, CopyOutlined, SaveOutlined } from "@ant-design/icons";
import CopyToClipboard from "react-copy-to-clipboard";
import dayjs from "dayjs";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AccountManagementCustomerService from "../services/AccountManagementCustomerService";
import {
  IAccountManagementCustomer,
  IAccountManagementPackageHistoryItem,
} from "../types/AccountManagementCustomer";
import { can, formatMoney, openNotification } from "../utils";
import Notes from "../components/AccountManagementCustomer/Notes";
import ProfitAndLoss from "../components/AccountManagementCustomer/ProfitAndLoss";
import "./AccountManagementCustomerDetail.css";

const PLACEHOLDER = "—";

const AVATAR_COLORS = ["#2563eb", "#7c3aed", "#db2777", "#ea580c", "#059669", "#0891b2"];

const getAvatarColor = (seed: string) => {
  const code = seed.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
};

const getInitials = (name: string) => {
  const parts = name.split(/\s+|@/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const formatDay = (value?: string) => {
  if (!value) return PLACEHOLDER;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : value;
};

const formatDayTime = (value?: string) => {
  if (!value) return PLACEHOLDER;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : value;
};

const money = (value?: number | null) => (value == null ? PLACEHOLDER : formatMoney(value));

// Kupon indirimi gelmediğinde fark liste fiyatı ile ödenen tutardan türetilir.
const getDiscount = (row: IAccountManagementPackageHistoryItem) => {
  if (row.couponDiscount != null) return row.couponDiscount;
  if (row.packagePrice != null && row.paidAmount != null && row.packagePrice > row.paidAmount) {
    return row.packagePrice - row.paidAmount;
  }
  return null;
};

const AccountManagementCustomerDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [customer, setCustomer] = useState<IAccountManagementCustomer | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const canUpdate = can("account-management-customers:update");

  const loadData = () => {
    if (!id) return;
    setLoading(true);
    AccountManagementCustomerService.getById(Number(id))
      .then((response) => {
        setCustomer(response.data);
        form.setFieldsValue({
          email: response.data.email,
          fullName: response.data.fullName,
          mongerlabUserId: response.data.mongerlabUserId,
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSave = async () => {
    if (!id) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      await AccountManagementCustomerService.update(Number(id), values);
      openNotification("success", "Success", "Record updated");
      loadData();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate("/account-management-customers", { state: location.state });
  };

  const history = useMemo(() => customer?.packageHistory ?? [], [customer]);

  const summary = useMemo(() => {
    const totalPaid = history.reduce((acc, item) => acc + (item.paidAmount ?? 0), 0);
    const totalDiscount = history.reduce((acc, item) => acc + (getDiscount(item) ?? 0), 0);
    const latestEnd = history.reduce<string | undefined>((latest, item) => {
      if (!item.endDate) return latest;
      if (!latest) return item.endDate;
      return dayjs(item.endDate).isAfter(dayjs(latest)) ? item.endDate : latest;
    }, undefined);
    return { count: history.length, totalPaid, totalDiscount, latestEnd };
  }, [history]);

  const displayName = customer?.fullName || customer?.email || "";

  const renderStatus = () => {
    if (!customer) return null;
    if (customer.hi5SyncError) {
      return (
        <span className="acmd-status acmd-status--unknown">
          <span className="acmd-status-dot" />
          Paket bilinmiyor
        </span>
      );
    }
    if (customer.packageActive) {
      return (
        <span className="acmd-status acmd-status--active">
          <span className="acmd-status-dot" />
          Paket aktif
        </span>
      );
    }
    return (
      <span className="acmd-status acmd-status--inactive">
        <span className="acmd-status-dot" />
        Paket pasif
      </span>
    );
  };

  const historyColumns: ColumnsType<IAccountManagementPackageHistoryItem> = [
    {
      title: "Abonelik",
      dataIndex: "subscriptionId",
      key: "subscriptionId",
      width: 120,
      render: (value, row) => (
        <div className="acmd-sub-id">
          <span className="acmd-sub-id-value">#{value}</span>
          {row.purchaseType && <span className="acmd-type-tag">{row.purchaseType}</span>}
        </div>
      ),
    },
    {
      title: "Dönem",
      key: "period",
      width: 150,
      render: (_value, row) => (
        <div className="acmd-period">
          <span className="acmd-period-start">{formatDay(row.startDate)}</span>
          <span className="acmd-period-end">bitiş {formatDay(row.endDate)}</span>
        </div>
      ),
    },
    {
      title: "Paket",
      dataIndex: "packagePrice",
      key: "packagePrice",
      width: 92,
      align: "right",
      render: (value: number) => (
        <span className={`acmd-money${value == null ? " acmd-money--muted" : ""}`}>
          {money(value)}
        </span>
      ),
    },
    {
      title: "Ödenen",
      dataIndex: "paidAmount",
      key: "paidAmount",
      width: 92,
      align: "right",
      render: (value: number) => (
        <span className={`acmd-money${value == null ? " acmd-money--muted" : ""}`}>
          {money(value)}
        </span>
      ),
    },
    {
      title: "İndirim",
      key: "coupon",
      width: 148,
      render: (_value, row) => {
        const discount = getDiscount(row);
        if (discount == null && !row.couponName) {
          return <span className="acmd-money acmd-money--muted">{PLACEHOLDER}</span>;
        }
        return (
          <div className="acmd-coupon">
            {row.couponName && (
              <span className="acmd-coupon-name" title={row.couponName}>
                {row.couponName}
              </span>
            )}
            {discount != null && (
              <span className="acmd-money acmd-money--discount">-{money(discount)}</span>
            )}
          </div>
        );
      },
    },
    {
      title: "Özellikler",
      key: "flags",
      width: 210,
      render: (_value, row) => {
        const flags: string[] = [];
        if (row.newUserDiscount) flags.push("Yeni kullanıcı");
        if (row.discounted) flags.push("İndirimli");
        if (row.userInitiated) flags.push("Kullanıcı başlattı");
        return (
          <div className="acmd-flags">
            {flags.map((flag) => (
              <span key={flag} className="acmd-flag acmd-flag--on">
                {flag}
              </span>
            ))}
            <span className={`acmd-flag${row.autoRenewalEnabled ? " acmd-flag--renew" : ""}`}>
              {row.autoRenewalEnabled ? "Oto yenileme" : "Yenileme kapalı"}
            </span>
          </div>
        );
      },
    },
    {
      title: "Oluşturma",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 110,
      render: (value: string) => (
        <Tooltip title={formatDayTime(value)}>
          <span className="acmd-date">{formatDay(value)}</span>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="acmd-page">
      <div className="acmd-header">
        <Button className="acmd-back-btn" icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Geri
        </Button>
        {loading && !customer ? (
          <Skeleton active paragraph={false} title={{ width: 220 }} />
        ) : (
          <>
            <Avatar
              size={40}
              className="acmd-header-avatar"
              style={{ backgroundColor: getAvatarColor(customer?.email || "?") }}
            >
              {getInitials(displayName || "?")}
            </Avatar>
            <div className="acmd-header-info">
              <div className="acmd-header-name">{customer?.fullName || PLACEHOLDER}</div>
              <div className="acmd-header-sub">
                <span>{customer?.email}</span>
                {customer?.email && (
                  <CopyToClipboard text={customer.email}>
                    <CopyOutlined
                      className="acmd-note-action"
                      style={{ width: "auto", height: "auto", cursor: "pointer" }}
                      onClick={() =>
                        openNotification("success", "Copied to clipboard", customer.email)
                      }
                    />
                  </CopyToClipboard>
                )}
              </div>
            </div>
            <div className="acmd-header-meta">
              {renderStatus()}
              {!customer?.hi5SyncError && customer?.packageEndDate && (
                <span className="acmd-date">Bitiş: {formatDay(customer.packageEndDate)}</span>
              )}
            </div>
          </>
        )}
      </div>

      <Row gutter={[12, 12]}>
        <Col xs={24} xl={16}>
          <div className="acmd-card">
            <div className="acmd-card-head">
              <span className="acmd-card-title">Müşteri Bilgileri</span>
            </div>
            <div className="acmd-card-body">
              {loading && !customer ? (
                <Skeleton active paragraph={{ rows: 3 }} />
              ) : canUpdate ? (
                <Form form={form} layout="vertical" className="acmd-form" onFinish={handleSave}>
                  <div className="acmd-form-grid">
                    <Form.Item
                      name="email"
                      label="E-posta"
                      rules={[{ required: true, type: "email", message: "Geçerli e-posta gerekli" }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item name="fullName" label="Ad Soyad">
                      <Input />
                    </Form.Item>
                    <Form.Item name="mongerlabUserId" label="Mongerlab User ID">
                      <InputNumber style={{ width: "100%" }} />
                    </Form.Item>
                  </div>
                  <div className="acmd-form-actions">
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<SaveOutlined />}
                      loading={saving}
                    >
                      Kaydet
                    </Button>
                  </div>
                </Form>
              ) : (
                <div className="acmd-info-grid">
                  <div className="acmd-info-item">
                    <div className="acmd-info-label">E-posta</div>
                    <div className="acmd-info-value" title={customer?.email}>
                      {customer?.email || PLACEHOLDER}
                    </div>
                  </div>
                  <div className="acmd-info-item">
                    <div className="acmd-info-label">Ad Soyad</div>
                    <div className="acmd-info-value">{customer?.fullName || PLACEHOLDER}</div>
                  </div>
                  <div className="acmd-info-item">
                    <div className="acmd-info-label">Mongerlab User ID</div>
                    <div className="acmd-info-value">
                      {customer?.mongerlabUserId ?? PLACEHOLDER}
                    </div>
                  </div>
                </div>
              )}

              <div className="acmd-info-grid" style={{ marginTop: 12 }}>
                <div className="acmd-info-item">
                  <div className="acmd-info-label">Oluşturma</div>
                  <div className="acmd-info-value">{formatDay(customer?.createdAt)}</div>
                </div>
                <div className="acmd-info-item">
                  <div className="acmd-info-label">Güncelleme</div>
                  <div className="acmd-info-value">{formatDay(customer?.updatedAt)}</div>
                </div>
                <div className="acmd-info-item">
                  <div className="acmd-info-label">Paket Bitiş</div>
                  <div className="acmd-info-value">
                    {customer?.hi5SyncError ? PLACEHOLDER : formatDay(customer?.packageEndDate)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>

        <Col xs={24} xl={8}>
          {id && <Notes customerId={Number(id)} />}
        </Col>
      </Row>

      {id && (
        <ProfitAndLoss
          customerId={Number(id)}
          customerName={customer?.fullName || customer?.email}
        />
      )}

      <div className="acmd-card">
        <div className="acmd-card-head">
          <span className="acmd-card-title">
            Paket Geçmişi
            <span className="acmd-card-title-count">{summary.count}</span>
          </span>
        </div>

        {summary.count > 0 && (
          <div className="acmd-summary">
            <div className="acmd-summary-item">
              <div className="acmd-summary-label">Abonelik</div>
              <div className="acmd-summary-value">{summary.count}</div>
            </div>
            <div className="acmd-summary-item">
              <div className="acmd-summary-label">Toplam Ödenen</div>
              <div className="acmd-summary-value">{formatMoney(summary.totalPaid)}</div>
            </div>
            <div className="acmd-summary-item">
              <div className="acmd-summary-label">Toplam İndirim</div>
              <div className="acmd-summary-value">{formatMoney(summary.totalDiscount)}</div>
            </div>
            <div className="acmd-summary-item">
              <div className="acmd-summary-label">Son Paket Bitiş</div>
              <div className="acmd-summary-value" style={{ fontSize: 15 }}>
                {formatDay(summary.latestEnd)}
              </div>
            </div>
          </div>
        )}

        <div className="acmd-card-body acmd-card-body--flush">
          <Table
            className="acmd-table"
            rowKey="subscriptionId"
            size="small"
            loading={loading}
            columns={historyColumns}
            dataSource={history}
            tableLayout="fixed"
            scroll={{ x: 922 }}
            rowClassName={(row) => (row.isLast ? "acmd-row-last" : "")}
            pagination={
              history.length > 10
                ? {
                    pageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50"],
                    showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} abonelik`,
                  }
                : false
            }
            locale={{
              emptyText: <div className="acmd-empty">Paket geçmişi bulunamadı</div>,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default AccountManagementCustomerDetail;
